import { useState, useMemo, useCallback } from 'react'

import EditIcon from '@mui/icons-material/Edit'
import SaveAltIcon from '@mui/icons-material/SaveAlt'
import SearchIcon from '@mui/icons-material/Search'
import WhatsApp from '@mui/icons-material/WhatsApp'
import Box from '@mui/material/Box'
import Button, { ButtonProps } from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Container from '@mui/material/Container'
import Typography from '@mui/material/Typography'
import {
  GridColDef,
  GridRenderCellParams,
  GridToolbarContainer,
  GridToolbarExportProps,
  useGridApiContext,
} from '@mui/x-data-grid'
import { useQuery } from '@tanstack/react-query'
import ConfirmationModal from 'components/ConfirmationModal'
import { ColumnItemProps } from 'components/DataTable'
import LoadingFullPage from 'components/LoadingFullPage'
import Table from 'components/Table'
import { QUERY_KEYS } from 'constants/keys'
import { ROUTES } from 'constants/routes'
import { useGlobalState } from 'contexts/global-state'
import { useNavigate } from 'react-router-dom'
import { ApiService } from 'services/api'
import {
  WHATSAPP_FREE_MESSAGE_MAX_LENGTH,
  getWhatsappMessageLength,
  limitWhatsappMessageInput,
} from 'utils/whatsapp-message'

import Delete from './delete'

export default function Teachers() {
  const navigate = useNavigate()

  const { data } = useQuery<Teacher[]>(QUERY_KEYS.TEACHERS.LIST, () =>
    ApiService.Teachers.getAll(),
  )

  const [deleteConfirmation, setDeleteConfirmation] = useState<
    Teacher | undefined
  >()

  const { openSuccessToast, openErrorToast } = useGlobalState()

  const [messageModal, setMessageModal] = useState(false)
  const [currentRow, setCurrentRow] = useState()
  const [currentName, setCurrentName] = useState()
  const [message, setMessage] = useState('')
  const [isSending, setIsSending] = useState(false)

  const [messageAllActiveTeachersModal, setMessageAllActiveTeachersModal] =
    useState(false)
  const [messageAllActiveTeachers, setMessageAllActiveTeachers] = useState('')

  const onConfirmMessage = useCallback(async () => {
    setIsSending(true)
    await ApiService.Teachers.sendMessage({
      id: Number(currentRow),
      message,
    })
      .then((response) => {
        openSuccessToast({ message: 'A mensagem foi enviada' })
        setIsSending(false)
        setMessageModal(false)
      })
      .catch((error) => {
        const message = error?.message || 'Erro ao enviar mensagem.'
        openErrorToast({ message })
        setIsSending(false)
        setMessageModal(false)
      })
  }, [message, currentRow, openErrorToast, openSuccessToast])

  const onConfirmMessageAllActiveTeachers = useCallback(async () => {
    setIsSending(true)
    await ApiService.Teachers.sendMessageAllActiveTeachers({
      message: messageAllActiveTeachers,
    })
      .then((response) => {
        openSuccessToast({ message: 'A mensagem foi enviada' })
        setIsSending(false)
        setMessageModal(false)
      })
      .catch((error) => {
        const message = error?.message || 'Erro ao enviar mensagem.'
        openErrorToast({ message })
        setIsSending(false)
        setMessageModal(false)
      })
  }, [messageAllActiveTeachers, openErrorToast, openSuccessToast])

  const columns = useMemo<GridColDef[]>(
    () => [
      { field: 'name', headerName: 'Nome', flex: 1 },
      { field: 'email', headerName: 'Email', flex: 1 },
      { field: 'phone', headerName: 'Telefone', flex: 1 },
      { field: 'cell_phone', headerName: 'Celular', flex: 1 },
      {
        field: 'actions',
        headerName: 'Ações',
        width: 100,
        sortable: false,
        exclude: true,
        renderCell: (params: GridRenderCellParams<Teacher>) => {
          return (
            <>
              <WhatsApp
                fontSize="small"
                onClick={() => {
                  setCurrentRow(params.row.id)
                  setCurrentName(params.row.name)
                  setMessage('')
                  setMessageModal(true)
                }}
              />
              <SearchIcon
                onClick={() => {
                  navigate(ROUTES.TEACHERS.VIEW(params.row.id))
                }}
              />
              <EditIcon
                onClick={() => {
                  navigate(ROUTES.TEACHERS.EDIT(params.row.id))
                }}
              />
            </>
          )
        },
      },
    ],
    [navigate],
  )

  const loadAllTeachers = useCallback(async () => {
    const entity = 'Teacher'
    const source = 'teachers/list'

    const newParams = {
      entity,
      source,
      page: 1,
      perPage: 0,
      selectFields: columns
        .filter((column: ColumnItemProps) => !column.exclude)
        .map((column: ColumnItemProps) => {
          const field = column.customField || column.field
          if (!column.relation) {
            return `${source}.${field}`
          }
          return field
        }),
      relations: columns
        .filter((column: ColumnItemProps) => !!column.relation)
        .map((column: ColumnItemProps) => ({
          ...column.relation,
        })),
    }
    const response = await ApiService.DataTable.getAll(newParams)
    return response.items
  }, [columns])

  function CustomToolbar(props: GridToolbarExportProps) {
    const { openErrorToast } = useGlobalState()
    const apiRef = useGridApiContext()

    const generateExportCsv = useCallback(async () => {
      const oldRows = apiRef.current.getSortedRows()
      const teachers = (await loadAllTeachers()) as { id: number }[]
      apiRef.current.setRows(teachers)
      apiRef.current.exportDataAsCsv({
        ...props.csvOptions,
        utf8WithBom: true,
        delimiter: ';',
      })
      apiRef.current.setRows(oldRows)
    }, [apiRef, props.csvOptions])

    const handleExport = useCallback(async () => {
      try {
        generateExportCsv()
      } catch (error: unknown) {
        openErrorToast({
          message: `Ocorreu um erro ao criar um registro na auditoria: ${
            (error as ApiError).message
          }`,
        })
      }
    }, [generateExportCsv, openErrorToast])

    const buttonBaseProps: ButtonProps = {
      color: 'primary',
      size: 'small',
      startIcon: <SaveAltIcon />,
    }
    return (
      <GridToolbarContainer>
        <Button {...buttonBaseProps} onClick={() => handleExport()}>
          Exportar
        </Button>
      </GridToolbarContainer>
    )
  }

  if (isSending) {
    return <LoadingFullPage />
  }

  return (
    <>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          py: 8,
        }}
      >
        <Container maxWidth={false}>
          <Box>
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
              }}
            >
              <Typography sx={{ m: 1 }} variant="h4">
                Professores
              </Typography>

              <Box sx={{ m: 1 }}>
                <Button
                  color="success"
                  variant="contained"
                  onClick={() => {
                    setMessageAllActiveTeachers('')
                    setMessageAllActiveTeachersModal(true)
                  }}
                  sx={{ mr: 1 }}
                >
                  <WhatsApp fontSize="small" style={{ marginRight: '5px' }} />
                  Todos
                </Button>
                <Button
                  color="primary"
                  variant="contained"
                  onClick={() => {
                    navigate(ROUTES.TEACHERS.CREATE)
                  }}
                >
                  Adicionar
                </Button>
              </Box>
            </Box>
          </Box>

          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Table
                rows={data}
                columns={columns}
                customToolbar={() => <CustomToolbar />}
              />
            </CardContent>
          </Card>
        </Container>
      </Box>

      <Delete
        opened={!!deleteConfirmation}
        closeModal={() => setDeleteConfirmation(undefined)}
        teacher={deleteConfirmation as Teacher}
      />

      <ConfirmationModal
        title={`Enviar Mensagem - ${currentName}`}
        opened={messageModal}
        onClose={() => setMessageModal(false)}
        onConfim={onConfirmMessage}
        onCancel={() => setMessageModal(false)}
      >
        <label htmlFor="message-wpp"></label>
        <textarea
          name="message-wpp"
          style={{ width: '100%' }}
          rows={5}
          value={message}
          onChange={(e) =>
            setMessage(limitWhatsappMessageInput(e.target.value))
          }
        ></textarea>
        <Typography
          variant="caption"
          sx={{ display: 'block', textAlign: 'right', mt: 0.5 }}
        >
          {getWhatsappMessageLength(message)}/{WHATSAPP_FREE_MESSAGE_MAX_LENGTH}
        </Typography>
      </ConfirmationModal>

      <ConfirmationModal
        title={`Enviar Mensagem - Todos os Professores`}
        opened={messageAllActiveTeachersModal}
        onClose={() => setMessageAllActiveTeachersModal(false)}
        onConfim={onConfirmMessageAllActiveTeachers}
        onCancel={() => setMessageAllActiveTeachersModal(false)}
      >
        <label htmlFor="message-wpp-all"></label>
        <textarea
          name="message-wpp-all"
          style={{ width: '100%' }}
          rows={5}
          value={messageAllActiveTeachers}
          onChange={(e) =>
            setMessageAllActiveTeachers(
              limitWhatsappMessageInput(e.target.value),
            )
          }
        ></textarea>
        <Typography
          variant="caption"
          sx={{ display: 'block', textAlign: 'right', mt: 0.5 }}
        >
          {getWhatsappMessageLength(messageAllActiveTeachers)}/
          {WHATSAPP_FREE_MESSAGE_MAX_LENGTH}
        </Typography>
      </ConfirmationModal>
    </>
  )
}
